import { useFirestoreOrderBy } from "../../firebase/useFirestore"
import { client } from "../../hooks/Client"
import Tooltip from "../common/Tooltip";
import { Auth } from '../../StateManagment/Auth';
import { useContext, useState, useEffect } from "react";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import DeleteModal from "../common/DeleteModal";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { db, timestamp } from "../../firebase/config.js"
import { v4 as uuid } from 'uuid';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import { useHistory } from "react-router-dom";
import OutputsCount from "../activities/OutputsCount.jsx";
import SelectTargetgroup from "../activities/SelectTargetgroup";
import Table from "../common/table/Table.jsx";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import Modal from 'react-modal';
import spinner from '../../images/spinner-ripple.svg';
import AddActivityFlow from "./add/AddActivityFlow.jsx";
import LinkedBadge from "../common/LinkedBadge.jsx";
import useSyncedLinks from "../../hooks/useSyncedLinks";
import useParentPortfolios from "../../hooks/useParentPortfolios";

import {
  activityTypes,
  activitySubtypesByType,
  activityTypeLabels,
  activitySubtypeLabels,
  deliveryModes,
  deliveryModeLabels,
  estimatedEffortTypes,
  estimatedEffortLabels,
  processActivity,
} from "../../helpers/dataHelpers/activityCategories";
import { DATE_FIELD_TITLE, OUTPUTS_FIELD_TITLE } from '../../helpers/events/defaultEventFieldTitles'

const EMPTY_FORM = {
  activityName: '',
  description: '',
  activityType: '',
  activitySubtype: '',
  deliveryMode: '',
  estimatedEffort: '',
}

/**
 * Reusable CRUD body for Activiteiten. Rendered both on the dedicated Activities
 * route and inline in the TOC navigator (F7). Owns its table, 5-step wizard,
 * cascade-delete (Outputs + EventForms) and the add-form cascade (EventForms +
 * EventFields). The route wrapper keeps the page chrome (PageHeader, Prev/Next).
 */
const ActivityManager = () => {
    // Context
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // State
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('123')
    const [deleteName, setDeleteName] = useState('')
    const [activityId, setActivityId] = useState('')
    const [dragArray, setDragArray] = useState([]);
    const [modalOpen, setModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState('add') // 'add' | 'edit'
    const [editingActivity, setEditingActivity] = useState(null)
    const [step, setStep] = useState(1)
    const [form, setForm] = useState(EMPTY_FORM)
    const [loading, setLoading] = useState(false)
    const [showAddContainer, setShowAddContainer] = useState(false)

    // Hooks
    const history = useHistory()

    // Firestore
    const activities = useFirestoreOrderBy('Activities', 'Position', 'asc')

    // Portfolio-paired activiteiten get a link badge (Synchronisations rows).
    const syncedLinks = useSyncedLinks()
    const { portfolioNameById } = useParentPortfolios()
    const linkedBadgeFor = (id) =>
        id && syncedLinks[id]
            ? <LinkedBadge portfolioName={portfolioNameById[syncedLinks[id]]} />
            : null

    Modal.setAppElement('#root')
    const modalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            minWidth: '400px',
            maxWidth: '560px',
            width: '90%',
        },
    }

    // Whenever Firestore data changes, update dragArray state
    useEffect(() => {
        setDragArray(activities);
    }, [activities]);

    // ─── Modal open/close ─────────────────────────────────────────────────────

    const openAddModal = () => {
        setModalMode('add')
        setEditingActivity(null)
        setForm(EMPTY_FORM)
        setStep(1)
        setModalOpen(true)
    }

    const openEditModal = (activity) => {
        setModalMode('edit')
        setEditingActivity(activity)
        setForm({
            activityName: activity.Activity || '',
            description: activity.Description || '',
            activityType: activity.activityType || '',
            activitySubtype: activity.activitySubtype || '',
            deliveryMode: activity.deliveryMode || '',
            estimatedEffort: activity.estimatedEffort || '',
        })
        setStep(1)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
    }

    // ─── Drag & drop ──────────────────────────────────────────────────────────

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(dragArray);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);

        setDragArray(items);

        items.forEach((item, idx) => {
          db.collection('Activities')
            .doc(item.docid)
            .update({ Position: idx + 1 });
        });

        setSaved('flex');
    };

    // ─── Delete ───────────────────────────────────────────────────────────────

    const deleteModalHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const deleteName = e.currentTarget.dataset.deletename
        const activityId = e.currentTarget.dataset.id

        setDeleteDocid(docid)
        setDeleteName(deleteName)
        setActivityId(activityId)
        setDeleteModal(!deleteModal)
    }

    // Soft delete (ADR-008) — never a hard .delete(). Cascades to the
    // activity's own Outputs + EventForms, which have no life outside it.
    // AI-CONTEXT: See docs/pitfalls.md#error-catch-all (ADR-008: soft deletes app-wide)
    const deleteActivity = async () => {
        const softDeleteFields = {
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        }

        await db.collection('Activities')
            .doc(deleteDocid)
            .update(softDeleteFields)

        const cascade = async (collection) => {
            const querySnapshot = await db.collection(collection)
                .where('ActivityID', '==', activityId)
                .get()
            await Promise.all(querySnapshot.docs.map((doc) => doc.ref.update(softDeleteFields)))
        }
        await Promise.all([cascade('Outputs'), cascade('EventForms')])

        setSaved('flex')
    }

    // ─── Inline title edit ────────────────────────────────────────────────────

    const activityHandler = (e) => {
        const title = e.target.value
        const docid = e.target.dataset.docid

        db.collection('Activities')
        .doc(docid)
        .update({ Activity: title })
        .then(() => { setSaved('flex') })
    }

    const descriptionHandler = (e) => {
        const description = e.target.value
        const docid = e.target.dataset.docid

        db.collection('Activities')
        .doc(docid)
        .update({ Description: description })
        .then(() => { setSaved('flex') })
    }

    // ─── Add activity form (unchanged) ────────────────────────────────────────

    const addActivityForm = async (id) => {
        const eventId = uuid();
        await db.collection('EventForms')
            .add({
            Title: `Nieuw formulier `,
            Description: 'Vul hier de resultaten van de activiteit in',
            Date: timestamp,
            ActivityID: id,
            ID: eventId,
            Compagny: client,
            CompagnyID: client,
            Version: '2',
            Position: activities.length + 1,
            Timestamp: timestamp,
            User: auth.ID,
            })

        await db.collection('EventFields').add({
            EventId: eventId,
            Type: 'date',
            ID: uuid(),
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Title: DATE_FIELD_TITLE,
            Position: 1
        })

        await db.collection('EventFields').add({
            EventId: eventId,
            Type: 'outputs',
            ID: uuid(),
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Title: OUTPUTS_FIELD_TITLE,
            Position: 2
        })

    }

    // ─── Save: add ────────────────────────────────────────────────────────────

    const saveActivity = async () => {
        setLoading(true)
        const id = uuid()
        const processed = processActivity(form)

        const docRef = db.collection('Activities').doc()
        await docRef.set({
            docid: docRef.id,
            Activity: processed.activityName,
            Description: form.description || '',
            ID: id,
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Position: activities.length + 1,
            // Taxonomy fields
            activityType: processed.activityType,
            activitySubtype: processed.activitySubtype,
            deliveryMode: processed.deliveryMode,
            estimatedEffort: processed.estimatedEffort,
        })

        await addActivityForm(id)

        setLoading(false)
        closeModal()
        setSaved('flex')
    }

    // ─── Save: edit ───────────────────────────────────────────────────────────

    const updateActivity = () => {
        setLoading(true)
        const processed = processActivity(form)

        db.collection('Activities')
        .doc(editingActivity.docid)
        .update({
            Activity: processed.activityName,
            Description: form.description || '',
            activityType: processed.activityType,
            activitySubtype: processed.activitySubtype,
            deliveryMode: processed.deliveryMode,
            estimatedEffort: processed.estimatedEffort,
        })
        .then(() => {
            setLoading(false)
            closeModal()
            setSaved('flex')
        })
    }

    // ─── Step form helpers ────────────────────────────────────────────────────

    const availableSubtypes = activitySubtypesByType[form.activityType] || []

    const selectSubtype = (subtype) => {
        setForm(prev => ({ ...prev, activitySubtype: subtype }))
    }

    const canProceed = [
        form.activityName.trim() !== '',  // step 1
        form.activityType !== '',          // step 2
        form.activitySubtype !== '',       // step 3
        form.deliveryMode !== '',          // step 4
        form.estimatedEffort !== '',       // step 5
    ]

    // ─── Table ────────────────────────────────────────────────────────────────

    const tableColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'activity', label: 'ACTIVITEIT' },
        { key: 'description', label: 'BESCHRIJVING', searchable: false },
        { key: 'type', label: 'TYPE', searchable: false },
        { key: 'outputs', label: 'OUTPUTS', searchable: false },
        { key: 'targetgroups', label: 'DOELGROEP(EN)', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ]

    return (
        <>
            <ReadOnlyGuard edit="strategy.edit">
                {(canEdit) => (
            <Table
                columns={tableColumns}
                items={dragArray || []}
                getItemKey={(activity) => activity.ID}
                renderRow={(activity) => (
                    <>
                        <td>
                            <DragIndicatorOutlinedIcon />
                        </td>
                        <td>
                            <Tooltip content={activity.Activity} width='100%' top='-60px'>
                                <input contentEditable type="text" data-docid={activity.docid} defaultValue={activity.Activity} placeholder='Titel' onChange={activityHandler} />
                            </Tooltip>
                            {linkedBadgeFor(activity.ID)}
                        </td>
                        <td className="common-table__cell--description">
                            <Tooltip content={activity.Description || ''} width='80%' top='-60px'>
                                <textarea
                                    className="common-table__description-textarea"
                                    data-docid={activity.docid}
                                    defaultValue={activity.Description || ''}
                                    placeholder="Beschrijving"
                                    onChange={descriptionHandler}
                                />
                            </Tooltip>
                        </td>
                        <td>
                            <div id='indicator-effect-icons-container'>
                                <span>
                                    {activity.activityType
                                        ? activityTypeLabels[activity.activityType] || activity.activityType
                                        : '—'}
                                </span>
                                <CreateOutlinedIcon
                                    style={{ fontSize: '16px', cursor: 'pointer', marginLeft: '6px', color: '#999' }}
                                    onClick={() => openEditModal(activity)}
                                />
                            </div>
                        </td>
                        <td>
                            <div id='indicator-effect-icons-container'>
                                <Tooltip content={'Output toevoegen'} top='-60px' width={'30px'}>
                                    <AddCircleOutlineOutlinedIcon className='table-delete-icon' onClick={() => history.push(`/${client}/addoutputs/${activity.ID}`)}/>
                                </Tooltip>
                                <Tooltip content={'Aantal outputs'} top='-60px' width='30px'>
                                    <p className="common-table__count-pill"><OutputsCount activityId={activity.ID} /></p>
                                </Tooltip>
                            </div>
                        </td>
                        <td>
                            <SelectTargetgroup activity={activity} />
                        </td>
                        <td>
                            {canEdit && (
                                <Tooltip content='Activiteit verwijderen' top='-60px'>
                                    <DeleteOutlinedIcon className='table-delete-icon' data-id={activity.ID} data-deletename={activity.Activity} data-docid={activity.docid} onClick={deleteModalHandler} />
                                </Tooltip>
                            )}
                        </td>
                    </>
                )}
                searchAccessor={(activity) => activity.Activity}
                searchPlaceholder="Zoeken op activiteit"
                emptyMessage="Geen activiteiten gevonden."
                draggable={true}
                droppableId="activities"
                onDragEnd={handleDragEnd}
                addRow={canEdit ? {
                    toolTipContent: 'Activiteit toevoegen',
                    onClick: () => setShowAddContainer(true),
                } : undefined}
            />
                )}
            </ReadOnlyGuard>

            <AddActivityFlow
                open={showAddContainer}
                setOpen={setShowAddContainer}
                onOwnAdd={openAddModal}
            />

            <DeleteModal deleteModal={deleteModal} setDeleteModal={setDeleteModal} deleteItem={deleteActivity} deleteName={deleteName}
  softDelete
  linkedNote="De outputs en registratieformulieren van deze activiteit gaan mee."
/>

            {/* ── Add / edit activity modal ── */}
            <Modal
                isOpen={modalOpen}
                onRequestClose={closeModal}
                style={modalStyles}
                contentLabel="Activiteit toevoegen"
            >
                <div>
                    <div id='delete-modal-text-container'>

                        <p style={{ color: '#999', fontSize: '13px', marginBottom: '4px' }}>
                            Stap {step} van 5
                        </p>

                        {/* ── Step 1: activityName ── */}
                        {step === 1 && (
                            <>
                                <h1>{modalMode === 'edit' ? 'Activiteit aanpassen' : 'Activiteit toevoegen'}</h1>
                                <p>Hoe heet deze activiteit?</p>
                                <input
                                    type="text"
                                    placeholder='bijv. Herstelcafé'
                                    value={form.activityName}
                                    onChange={(e) => {
                                        // Waarde EERST uitlezen. `e.target` is het levende
                                        // DOM-element; leest de updater hem pas als React al
                                        // draait, dan staat de oude waarde er alweer in en
                                        // schrijf je die terug.
                                        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                                        const value = e.target.value
                                        setForm(prev => ({ ...prev, activityName: value }))
                                    }}
                                />
                                <p>Beschrijving (optioneel)</p>
                                <textarea
                                    rows="3"
                                    placeholder='Beschrijf de activiteit'
                                    value={form.description}
                                    onChange={(e) => {
                                        // Zie de opmerking bij het naamveld hierboven: de waarde
                                        // moet buiten de updater vandaan komen.
                                        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                                        const value = e.target.value
                                        setForm(prev => ({ ...prev, description: value }))
                                    }}
                                />
                            </>
                        )}

                        {/* ── Step 2: activityType ── */}
                        {step === 2 && (
                            <>
                                <h1>Type activiteit</h1>
                                <p>Selecteer het activiteitstype</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                    {activityTypes.map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setForm(prev => ({ ...prev, activityType: type, activitySubtype: '' }))}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                border: '1px solid #ccc',
                                                background: form.activityType === type ? '#2196f3' : '#fff',
                                                color: form.activityType === type ? '#fff' : '#333',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                            }}
                                        >
                                            {activityTypeLabels[type]}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* ── Step 3: activitySubtype ── */}
                        {step === 3 && (
                            <>
                                <h1>Subtype</h1>
                                <p>Selecteer een subtype voor <strong>{activityTypeLabels[form.activityType]}</strong></p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                    {availableSubtypes.map(sub => (
                                        <button
                                            key={sub}
                                            onClick={() => selectSubtype(sub)}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                border: '1px solid #ccc',
                                                background: form.activitySubtype === sub ? '#2196f3' : '#fff',
                                                color: form.activitySubtype === sub ? '#fff' : '#333',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                            }}
                                        >
                                            {activitySubtypeLabels[sub] || sub}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* ── Step 4: deliveryMode ── */}
                        {step === 4 && (
                            <>
                                <h1>Uitvoering</h1>
                                <p>Hoe wordt deze activiteit uitgevoerd?</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                    {deliveryModes.map(mode => (
                                        <button
                                            key={mode}
                                            onClick={() => setForm(prev => ({ ...prev, deliveryMode: mode }))}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                border: '1px solid #ccc',
                                                background: form.deliveryMode === mode ? '#2196f3' : '#fff',
                                                color: form.deliveryMode === mode ? '#fff' : '#333',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                            }}
                                        >
                                            {deliveryModeLabels[mode]}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* ── Step 5: estimatedEffort ── */}
                        {step === 5 && (
                            <>
                                <h1>Intensiteit</h1>
                                <p>Hoe intensief is deze activiteit voor deelnemers?</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                    {estimatedEffortTypes.map(effort => (
                                        <button
                                            key={effort}
                                            onClick={() => setForm(prev => ({ ...prev, estimatedEffort: effort }))}
                                            style={{
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: '1px solid #ccc',
                                                background: form.estimatedEffort === effort ? '#2196f3' : '#fff',
                                                color: form.estimatedEffort === effort ? '#fff' : '#333',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                textAlign: 'left',
                                            }}
                                        >
                                            {estimatedEffortLabels[effort]}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                    </div>

                    {/* ── Buttons ── */}
                    <div id='delete-modal-button-container'>
                        <button
                            id='modal-cancel-button'
                            onClick={step === 1 ? closeModal : () => setStep(s => s - 1)}
                            style={{ display: loading ? 'none' : 'block' }}
                        >
                            {step === 1 ? 'Annuleren' : 'Vorige'}
                        </button>

                        {step < 5 && (
                            <button
                                id='modal-save-button'
                                onClick={() => setStep(s => s + 1)}
                                disabled={!canProceed[step - 1]}
                                style={{ display: loading ? 'none' : 'block' }}
                            >
                                Volgende
                            </button>
                        )}

                        {step === 5 && (
                            <button
                                id='modal-save-button'
                                onClick={modalMode === 'edit' ? updateActivity : saveActivity}
                                disabled={!canProceed[step - 1]}
                                style={{ display: loading ? 'none' : 'block' }}
                            >
                                Opslaan
                            </button>
                        )}

                        <img src={spinner} alt="" style={{ display: loading ? 'block' : 'none' }} />
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default ActivityManager
